<template>
  <div class="create-category-dialog">
    <header class="dialog-header">
      <div class="dialog-eyebrow">New</div>
      <h2 class="dialog-title">Marker Category</h2>
      <p class="dialog-subtitle">Add a custom marker category to your organization.</p>
    </header>

    <v-form ref="form" @submit.prevent="handleSubmit">
      <div class="form-grid">
        <v-text-field v-model="label" label="Name" variant="outlined" density="comfortable" hide-details="auto"
          class="input-field" required />
        <v-text-field v-model="description" label="Description" variant="outlined" density="comfortable"
          hide-details="auto" class="input-field" required />

        <v-select v-model="shape" :items="shapeItems" item-title="label" item-value="value" label="Marker shape"
          variant="outlined" density="comfortable" hide-details class="input-field">
          <template #prepend-inner>
            <v-icon size="18" :color="color">{{ shapeIcon }}</v-icon>
          </template>
          <template #item="{ props: itemProps, item }">
            <v-list-item v-bind="itemProps" :title="item.raw.label">
              <template #prepend>
                <v-icon size="18">{{ item.raw.icon }}</v-icon>
              </template>
            </v-list-item>
          </template>
        </v-select>

        <div class="color-field">
          <span class="color-field-label">Marker colour</span>
          <div class="swatches">
            <button v-for="c in colorSwatches" :key="c" type="button" class="swatch"
              :class="{ 'is-selected': sameColor(c, color) }" :style="{ background: c }" :title="c"
              @click="color = c" />
            <label class="swatch custom-swatch" :style="{ background: color }" title="Custom colour">
              <v-icon size="14" color="#fff">mdi-plus</v-icon>
              <input type="color" v-model="color" class="color-input" />
            </label>
          </div>
        </div>
      </div>

      <div class="helper-card">
        <v-icon size="18" color="#64ffda">mdi-information-outline</v-icon>
        <span>A custom category may not have a supporting detection model. Manual use only.</span>
      </div>

      <div class="actions">
        <button type="button" class="ghost-btn" :disabled="isSubmitting" @click="handleCancel">Cancel</button>
        <button type="submit" class="primary-btn" :disabled="!isFormComplete || isSubmitting">
          <v-progress-circular v-if="isSubmitting" indeterminate size="16" width="2" color="#071514" />
          <span v-else>Create Category</span>
        </button>
      </div>
    </v-form>
  </div>
</template>

<script setup>
import { ref, computed, defineEmits, onMounted } from 'vue'
import CreateMarkerTypeModel from '@/domain/marker/CreateMarkerTypeModel'
import MarkerService from '@/services/MarkerService'
import { useOrganizationStore } from '@/store/organizationStore'
import { MARKER_SHAPES, MARKER_COLOR_SWATCHES, DEFAULT_MARKER_SHAPE, DEFAULT_MARKER_COLOR } from '@/lib/markerShapeFactory'

const emit = defineEmits(['onCategoryCreated', 'onCloseWindow'])

const label = ref('')
const description = ref('')
const shape = ref(DEFAULT_MARKER_SHAPE)
const color = ref(DEFAULT_MARKER_COLOR)
const isSubmitting = ref(false)
const organizationStore = useOrganizationStore()
const orgId = ref('')
const form = ref(null)

const shapeItems = MARKER_SHAPES
const colorSwatches = MARKER_COLOR_SWATCHES
const shapeIcon = computed(() => MARKER_SHAPES.find(s => s.value === shape.value)?.icon || 'mdi-circle-outline')
const sameColor = (a, b) => (a || '').toLowerCase() === (b || '').toLowerCase()

const isFormComplete = computed(() => label.value.trim() && description.value.trim())

onMounted(() => { orgId.value = organizationStore.authZeroOrgId || '' })

function resetForm() {
  label.value = ''
  description.value = ''
  shape.value = DEFAULT_MARKER_SHAPE
  color.value = DEFAULT_MARKER_COLOR
  form.value?.resetValidation?.()
}

function handleCancel() {
  resetForm()
  emit('onCloseWindow')
}

async function handleSubmit() {
  if (!isFormComplete.value || isSubmitting.value) return
  isSubmitting.value = true
  try {
    const createdMarkerType = new CreateMarkerTypeModel(organizationStore.organizationId, label.value, description.value, color.value, shape.value);
    await MarkerService.CreateMarkerType(createdMarkerType);
  } finally {
    emit('onCategoryCreated');
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.create-category-dialog {
  padding: 4px;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: min(420px, 80vw);
}

.dialog-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dialog-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8aa0aa;
}

.dialog-title {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  color: #fff;
}

.dialog-subtitle {
  font-size: 0.9rem;
  color: #b0bec5;
  margin: 0;
  line-height: 1.5;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.input-field :deep(.v-field) {
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
}

.color-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.color-field-label {
  font-size: 0.78rem;
  color: #b0bec5;
  font-weight: 600;
}

.swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.swatch {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 2px solid rgba(255, 255, 255, 0.12);
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.1s ease, border-color 0.1s ease;
}

.swatch:hover {
  transform: scale(1.08);
}

.swatch.is-selected {
  border-color: #fff;
  box-shadow: 0 0 0 2px rgba(100, 255, 218, 0.5);
}

.custom-swatch {
  position: relative;
  overflow: hidden;
}

.color-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.helper-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 18px 0 8px;
  padding: 12px 14px;
  background: rgba(100, 255, 218, 0.06);
  border: 1px solid rgba(100, 255, 218, 0.15);
  border-radius: 10px;
  font-size: 0.85rem;
  color: #cfe4e8;
  line-height: 1.5;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
}

.ghost-btn {
  padding: 10px 18px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: #cfd8dc;
  font-weight: 600;
  font: inherit;
  cursor: pointer;
  transition: background 0.15s ease;
}

.ghost-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
}

.primary-btn {
  padding: 10px 22px;
  border-radius: 10px;
  background: var(--duallo-accent-gradient);
  color: #071514;
  font-weight: 700;
  border: none;
  font: inherit;
  cursor: pointer;
  min-width: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.primary-btn:disabled,
.ghost-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
